import React, { ChangeEvent, KeyboardEvent } from 'react';
import { numDigits, cutLastDigit } from '../../shared/util/functions';
import { VidebisButton } from '../../shared/components/videbis-button/videbis-button';
import { ConfigTitle } from './config-title';
import '../styles/user-data.scss';

const MAX_AGE = 110;
const MIN_AGE = 3;

const MAX_HEIGHT = 220;
const MIN_HEIGHT = 80;

interface Props {
    onPress: () => void;
    onBack: () => void;
    height?: number;
    onHeightChange: (value: number) => void;
    yearOfBirth?: number;
    onYearOfBirthChange: (value: number) => void;
    onInvalidData: (value: boolean) => void;
}

interface State {
    showHeightError: boolean;
    showYearOfBirthError: boolean;
}

export class UserData extends React.PureComponent<Props, State> {
    state: State = {
        showHeightError: false,
        showYearOfBirthError: false
    };

    private readonly _thisYear: number;

    constructor(props: Readonly<Props>) {
        super(props);
        this._thisYear = (new Date()).getFullYear();
    }

    componentDidUpdate(prevProps: Props): void {
        if (this.props !== prevProps) {
            this.validateUserInput();
            this.handleDisableContinue(
                this.state.showHeightError ||
                this.state.showYearOfBirthError ||
                !this.props.height ||
                !this.props.yearOfBirth
            );
        }
    }

    private handleHeightChange = (event: ChangeEvent<HTMLInputElement>): void => {
        let height: number = parseInt(event.target.value, 10);
        if (numDigits(height) > 3) height = cutLastDigit(height);
        this.props.onHeightChange(height);
    };

    private handleYearOfBirthChange = (event: ChangeEvent<HTMLInputElement>): void => {
        let yearOfBirth: number = parseInt(event.target.value, 10);
        if (numDigits(yearOfBirth) > 4) yearOfBirth = cutLastDigit(yearOfBirth);
        this.props.onYearOfBirthChange(yearOfBirth);
    };

    private handleDisableContinue = (shouldDisable: boolean): void => {
        this.props.onInvalidData(shouldDisable);
    };

    private validateHeightInput = (): void => {
        if (!this.props.height) {
            this.setState({ showHeightError: true });
            return;
        }

        if (this.props.height < MIN_HEIGHT || this.props.height > MAX_HEIGHT) {
            if (!this.state.showHeightError) {
                this.setState({ showHeightError: true });
            }
        } else if (this.state.showHeightError) {
            this.setState({ showHeightError: false });
        }
    };

    private validateYearOfBirthInput = (): void => {
        const now: Date = new Date();
        const thisYear: number = now.getFullYear();

        if (!this.props.yearOfBirth) {
            this.setState({ showYearOfBirthError: true });
            return;
        }

        if (this.props.yearOfBirth < thisYear - MAX_AGE || this.props.yearOfBirth > thisYear - MIN_AGE) {
            if (!this.state.showYearOfBirthError) {
                this.setState({ showYearOfBirthError: true });
            }
        } else if (this.state.showYearOfBirthError) {
            this.setState({ showYearOfBirthError: false });
        }
    };

    private validateUserInput = (): void => {
        if (!this.state.showHeightError) {
            if (this.props.height && numDigits(this.props.height) >= 3) {
                this.validateHeightInput();
            }
        } else {
            this.validateHeightInput();
        }

        if (!this.state.showYearOfBirthError) {
            if (this.props.yearOfBirth && numDigits(this.props.yearOfBirth) >= 4) {
                this.validateYearOfBirthInput();
            }
        } else {
            this.validateYearOfBirthInput();
        }
    };

    private handleInputKeyPress = (e: KeyboardEvent): void => {
        if (e.key === 'Enter') {
            if (document.activeElement) {
                this.validateHeightInput();
                this.validateYearOfBirthInput();
                (document.activeElement as HTMLInputElement).blur();
                setTimeout(() => {
                    if (!this.shouldDisableNextButton()) {
                        this.props.onPress();
                    }
                }, 80);
            }
        }
    };

    private shouldDisableNextButton = (): boolean => {
        return (
            this.state.showHeightError ||
            this.state.showYearOfBirthError ||
            !this.props.height ||
            !this.props.yearOfBirth
        );
    };

    private handleNextButtonClick = (disabled: boolean): void => {
        if (disabled) {
            this.validateHeightInput();
            this.validateYearOfBirthInput();
            return;
        }
        this.validateUserInput();
        this.props.onPress();
    };

    render(): any {
        return (
            <section className={'limit-width'}>
                <ConfigTitle title={'Kalibrierung'} subtitle={'3. Schritt: Persönliche Einstellungen'} />
                <div className={'user-data__text'}>
                    <div className={'user-data__text__height'}>
                        <div className={'user-data__text__height__data'}>
                            <label className={'standard-text'} htmlFor={'user-data__height'}>Meine Körpergröße:</label><br />
                            <div className={'user-data__input--wrapper'}>
                                <input
                                    name={'height-input'}
                                    type={'number'}
                                    id={'user-data__height'}
                                    value={this.props.height || ''}
                                    onChange={this.handleHeightChange}
                                    onBlur={this.validateHeightInput}
                                    onKeyPress={this.handleInputKeyPress}
                                    autoFocus={true}
                                />
                            </div>
                            <span className={'standard-text'}>cm</span>
                        </div>
                        <div
                            className={'user-data__text__error'}
                            style={{
                                display:
                                    this.state.showHeightError || this.state.showYearOfBirthError ? 'inherit' : 'none',
                                visibility:
                                    !this.state.showHeightError && this.state.showYearOfBirthError ? 'hidden' : 'visible'
                            }}
                        >
                            Geben Sie Ihre Körpergröße in cm an, z.B.: 170 (zwischen {MIN_HEIGHT} und {MAX_HEIGHT})
                        </div>
                    </div>

                    <div className={'user-data__text__age'}>
                        <div className={'user-data__text__age__data'}>
                            <label className={'standard-text'} htmlFor={'user-data__age'}>
                                Mein Geburtsjahr:
                            </label><br />
                            <div className={'user-data__input--wrapper'}>
                                <input
                                    name={'year-of-birth-input'}
                                    type={'number'}
                                    id={'user-data__age'}
                                    value={this.props.yearOfBirth || ''}
                                    onChange={this.handleYearOfBirthChange}
                                    onBlur={this.validateYearOfBirthInput}
                                    onKeyPress={this.handleInputKeyPress}
                                />
                            </div>
                        </div>
                        <div
                            className={'user-data__text__error'}
                            style={{
                                display:
                                    this.state.showYearOfBirthError ||
                                        this.state.showHeightError ? 'inherit' : 'none',
                                visibility:
                                    !this.state.showYearOfBirthError &&
                                        this.state.showHeightError ? 'hidden' : 'visible'
                            }}
                        >
                            Geben Sie Ihr Geburtsjahr an, z.B.: 1970 (zwischen {this._thisYear - MAX_AGE} und {this._thisYear - MIN_AGE})
                        </div>
                    </div>
                </div>

                <div className={'user-data__buttons'}>
                    <VidebisButton
                        onPress={this.props.onBack}
                        text={'Zurück'}
                        bgColor={'#ffffff'}
                        txtColor={'#0c23fb'} />
                    <VidebisButton
                        disabled={this.shouldDisableNextButton()}
                        onPress={() => this.handleNextButtonClick(false)}
                        onDisabledPressed={() => this.handleNextButtonClick(true)}
                    />
                </div>
            </section >
        );
    }
}
